import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, QrCode, Link, CreditCard, Upload, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useCurrencySettings } from "@/hooks/useCurrencySettings";

interface PaymentMethod {
  id: string;
  method_type: string;
  method_name: string;
  is_active: boolean;
  configuration: any;
  qr_code_url?: string;
  payment_link?: string;
  bank_details?: any;
}

const PaymentMethodsManager: React.FC = () => {
  useUiLanguage();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation("payments");
  const { defaultCurrency } = useCurrencySettings();

  const [formData, setFormData] = useState({
    method_type: "bank_transfer",
    method_name: "",
    is_active: true,
    iban: "",
    recipient: "",
    address: "",
    city: "",
    reference: "",
    payment_link: "",
    qr_code_file: null as File | null,
  });

  React.useEffect(() => {
    fetchPaymentMethods();
    // Pre-populate with default dojo name if available
    loadDojoSettings();
  }, []);

  // Listen for impersonation state changes
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "impersonationState") {
        fetchPaymentMethods();
        loadDojoSettings();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const loadDojoSettings = async () => {
    // Get effective dojo ID (handles impersonation)
    const impersonationState = sessionStorage.getItem("impersonationState");
    let effectiveDojoId = profile?.dojo_id;

    if (impersonationState) {
      try {
        const parsed = JSON.parse(impersonationState);
        if (parsed.active && parsed.dojoId) {
          effectiveDojoId = parsed.dojoId;
        }
      } catch (error) {
        console.warn("Failed to parse impersonation state:", error);
      }
    }

    if (!effectiveDojoId) return;

    try {
      const { data } = await supabase
        .from("dojo_specific_settings")
        .select("dojo_name")
        .eq("dojo_id", effectiveDojoId)
        .single();

      if (data?.dojo_name && !formData.recipient) {
        setFormData((prev) => ({
          ...prev,
          recipient: data.dojo_name,
        }));
      }
    } catch (error) {
      // Ignore error, just means no settings yet
    }
  };

  const fetchPaymentMethods = async () => {
    // Get effective dojo ID (handles impersonation)
    const impersonationState = sessionStorage.getItem("impersonationState");
    let effectiveDojoId = profile?.dojo_id;

    if (impersonationState) {
      try {
        const parsed = JSON.parse(impersonationState);
        if (parsed.active && parsed.dojoId) {
          effectiveDojoId = parsed.dojoId;
        }
      } catch (error) {
        console.warn("Failed to parse impersonation state:", error);
      }
    }

    if (!effectiveDojoId) return;

    const { data, error } = await supabase
      .from("payment_methods")
      .select("*")
      .eq("dojo_id", effectiveDojoId)
      .order("created_at", { ascending: true });

    if (error) {
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch payment methods"),
        variant: "destructive",
      });
      return;
    }

    setPaymentMethods(data || []);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Get effective dojo ID (handles impersonation)
    const impersonationState = sessionStorage.getItem("impersonationState");
    let effectiveDojoId = profile?.dojo_id;

    if (impersonationState) {
      try {
        const parsed = JSON.parse(impersonationState);
        if (parsed.active && parsed.dojoId) {
          effectiveDojoId = parsed.dojoId;
        }
      } catch (error) {
        console.warn("Failed to parse impersonation state:", error);
      }
    }

    if (!effectiveDojoId) return;

    setIsLoading(true);

    try {
      let qr_code_url = editingMethod?.qr_code_url;

      // Upload QR code if provided
      if (formData.qr_code_file) {
        const fileExt = formData.qr_code_file.name.split(".").pop();
        const fileName = `${effectiveDojoId}-${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("payment-qr-codes")
          .upload(`qr-codes/${fileName}`, formData.qr_code_file);

        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage
          .from("payment-qr-codes")
          .getPublicUrl(`qr-codes/${fileName}`);

        qr_code_url = publicUrl;
      }

      const methodData = {
        dojo_id: effectiveDojoId,
        method_type: formData.method_type,
        method_name: formData.method_name,
        is_active: formData.is_active,
        qr_code_url,
        payment_link: formData.payment_link || null,
        bank_details:
          formData.method_type === "bank_transfer"
            ? {
                iban: formData.iban,
                recipient: formData.recipient,
                address: formData.address,
                city: formData.city,
                reference: formData.reference,
              }
            : null,
        configuration: {
          currency: defaultCurrency,
          instructions: "Complete your payment using the method below",
        },
      };

      if (editingMethod) {
        const { error } = await supabase
          .from("payment_methods")
          .update(methodData)
          .eq("id", editingMethod.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("payment_methods")
          .insert([methodData]);

        if (error) throw error;
      }

      toast({
        title: uiText("Success"),
        description: uiText("Payment method {{value0}} successfully", { value0: editingMethod ? "updated" : "created" }),
      });

      setIsDialogOpen(false);
      setEditingMethod(null);
      resetForm();
      fetchPaymentMethods();
    } catch (error) {
      console.error("Error saving payment method:", error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to save payment method"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      method_type: "bank_transfer",
      method_name: "Bank Transfer",
      is_active: true,
      iban: "",
      recipient: "",
      address: "",
      city: "",
      reference:
        "Please include your name and training purpose in the transfer reference",
      payment_link: "",
      qr_code_file: null,
    });
  };

  const handleEdit = (method: PaymentMethod) => {
    setEditingMethod(method);
    setFormData({
      method_type: method.method_type,
      method_name: method.method_name,
      is_active: method.is_active,
      iban: method.bank_details?.iban || "",
      recipient: method.bank_details?.recipient || "",
      address: method.bank_details?.address || "",
      city: method.bank_details?.city || "",
      reference: method.bank_details?.reference || "",
      payment_link: method.payment_link || "",
      qr_code_file: null,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("payment_methods")
      .delete()
      .eq("id", id);

    if (error) {
      toast({
        title: uiText("Error"),
        description: uiText("Failed to delete payment method"),
        variant: "destructive",
      });
      return;
    }

    toast({
      title: uiText("Success"),
      description: uiText("Payment method deleted successfully"),
    });

    fetchPaymentMethods();
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case "qr_code":
        return <QrCode className="h-4 w-4" />;
      case "payment_link":
        return <Link className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{uiText("Payment Methods")}</h2>
          <p className="text-muted-foreground">
            {uiText("Configure payment options for your students")}</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditingMethod(null);
                resetForm();
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {uiText("Add Payment Method")}</Button>
          </DialogTrigger>

          <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-3 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">
                {editingMethod ? uiText("Edit") : uiText("Add")} {' '}{uiText("Payment Method")}</DialogTitle>
              <DialogDescription className="text-sm">
                {uiText("Configure how students can make payments to your dojo")}</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="method_name">{uiText("Method Name")}</Label>
                  <Input
                    id="method_name"
                    value={formData.method_name}
                    onChange={(e) =>
                      setFormData({ ...formData, method_name: e.target.value })
                    }
                    placeholder={uiText("e.g., Bank Transfer, PayPal")}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="method_type">{uiText("Method Type")}</Label>
                  <Select
                    value={formData.method_type}
                    onValueChange={(value: any) =>
                      setFormData({ ...formData, method_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">
                        {uiText("Bank Transfer")}</SelectItem>
                      <SelectItem value="qr_code">{uiText("QR Code Payment")}</SelectItem>
                      <SelectItem value="payment_link">{uiText("Payment Link")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.method_type === "bank_transfer" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="iban">{uiText("IBAN")}</Label>
                      <Input
                        id="iban"
                        value={formData.iban}
                        onChange={(e) =>
                          setFormData({ ...formData, iban: e.target.value })
                        }
                        placeholder={uiText("CH50 0070 0114 9023 3101 7")}
                        className="input-polished"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="recipient">{uiText("Recipient")}</Label>
                      <Input
                        id="recipient"
                        value={formData.recipient}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            recipient: e.target.value,
                          })
                        }
                        placeholder={uiText("Dojo Name")}
                        className="input-polished"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="address">{uiText("Address")}</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) =>
                          setFormData({ ...formData, address: e.target.value })
                        }
                        placeholder={uiText("Street Address")}
                        className="input-polished"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">{uiText("City")}</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) =>
                          setFormData({ ...formData, city: e.target.value })
                        }
                        placeholder={uiText("City")}
                        className="input-polished"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reference">{uiText("Reference Instructions")}</Label>
                    <Textarea
                      id="reference"
                      value={formData.reference}
                      onChange={(e) =>
                        setFormData({ ...formData, reference: e.target.value })
                      }
                      placeholder={uiText("Please include your name and purpose in the transfer reference")}
                    />
                  </div>
                </div>
              )}

              {formData.method_type === "payment_link" && (
                <div className="space-y-2">
                  <Label htmlFor="payment_link">{uiText("Payment Link")}</Label>
                  <Input
                    id="payment_link"
                    type="url"
                    value={formData.payment_link}
                    onChange={(e) =>
                      setFormData({ ...formData, payment_link: e.target.value })
                    }
                    placeholder="https://paypal.me/yourdojo"
                  />
                </div>
              )}

              {(formData.method_type === "qr_code" ||
                formData.method_type === "bank_transfer") && (
                <div className="space-y-2">
                  <Label htmlFor="qr_code">{uiText("QR Code Image")}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="qr_code"
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          qr_code_file: e.target.files?.[0] || null,
                        })
                      }
                    />
                    <Upload className="h-4 w-4" />
                  </div>
                  {editingMethod?.qr_code_url && (
                    <div className="mt-2">
                      <img
                        src={editingMethod.qr_code_url}
                        alt={uiText("Current QR Code")}
                        className="w-20 h-20 object-cover rounded border"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
                <Label htmlFor="is_active">{uiText("Active")}</Label>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-border-subtle">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="w-full sm:w-auto"
                >
                  {uiText("Cancel")}</Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="btn-dojo-primary w-full sm:w-auto"
                >
                  {isLoading
                    ? uiText("Saving...")
                    : editingMethod
                      ? uiText("Update")
                      : uiText("Create")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {paymentMethods.map((method) => (
          <Card key={method.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-2">
                {getMethodIcon(method.method_type)}
                <CardTitle className="text-lg">{method.method_name}</CardTitle>
                {!method.is_active && (
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                    {uiText("Inactive")}</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(method)}
                >
                  {uiText("Edit")}</Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(method.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {uiText("Type:")}{' '}{method.method_type.replace("_", " ").toUpperCase()}
                {method.bank_details?.iban && (
                  <span className="block mt-1">
                    {uiText("IBAN:")}{' '}{method.bank_details.iban}
                  </span>
                )}
                {method.payment_link && (
                  <span className="block mt-1">
                    {uiText("Link:")}{' '}{method.payment_link}
                  </span>
                )}
              </CardDescription>
            </CardContent>
          </Card>
        ))}

        {paymentMethods.length === 0 && (
          <Card>
            <CardContent className="text-center py-6">
              <p className="text-muted-foreground">
                {uiText("No payment methods configured yet.")}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {uiText("Add your first payment method to get started.")}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsManager;
